import { createFileRoute, Outlet } from '@tanstack/react-router'
import { MailIcon, AlertTriangleIcon, LayoutGridIcon, MoveHorizontalIcon } from 'lucide-react'
import { usePreviewMode } from '@/components/custom/route-transition/surface/route-preview'
import { TransitionTabsSurface } from './-components/shared/transition-tabs'
import { PanelBlockPending } from './-components/app-layout-skeleton'

// Layout for the super-admin dev playground. Each section is its OWN route under here, and the tab bar is a
// TRANSITION TABS SURFACE (shared/transition-tabs.tsx): real <Link>s whose underline rides the swipe, and four
// swipeable panes on a phone. The bare `/dev-resources` redirects to the emails tab.
//
// `scrollablePanes` is the load-bearing half: this page lives in the app's FIXED-HEIGHT shell and a pager clips
// its own vertical overflow so a vertical drag falls through, so one shared scroll container around the <Outlet/>
// would leave everything past one pane-height unreachable. Each pane owns its scroller, like the report tabs.
export const Route = createFileRoute('/_app/dev-resources')({
  component: RouteComponent,
  pendingComponent: PanelBlockPending,
})

const DEV_RESOURCES_SCOPE = '/dev-resources'

const TABS = [
  { segment: 'emails', label: 'Email templates', icon: MailIcon },
  { segment: 'errors', label: 'Captured errors', icon: AlertTriangleIcon },
  { segment: 'components', label: 'Components', icon: LayoutGridIcon },
  { segment: 'transitions', label: 'Transitions', icon: MoveHorizontalIcon },
] as const

// The pane: the page padding and the centred max-width the header above shares, now INSIDE the pane so they
// travel with the page (padding on a container outside the stage doesn't). The max-width is applied to the
// pane's CONTENT rather than the pane, because the pane is the slide's opaque cover — narrowing it would let
// the pane beneath show through the gutters on a wide window. Each route renders a single root element.
const PANE = 'px-6 pt-6 pb-6 sm:px-8 sm:pt-8 sm:pb-8 [&>*]:mx-auto [&>*]:w-full [&>*]:max-w-6xl'

function RouteComponent() {
  // Inside a live preview pinned at this surface (a tab swipe) or below it (the transition repro's own pagers,
  // see dev-resources.transitions.tsx) this layout is the pane CONTENT — the header and the tab strip are live
  // chrome around the pager and must not render into every preview slot.
  if (usePreviewMode(DEV_RESOURCES_SCOPE) === 'passthrough') return <Outlet />

  return (
    <div className="flex h-full flex-col">
      <div className="px-6 pt-6 sm:px-8 sm:pt-8">
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-1">
          <h1 className="text-2xl font-bold">Developer resources</h1>
          <p className="text-muted-foreground text-sm">
            Internal dev playground — preview email templates, triage captured errors, and browse UI components.
          </p>
        </div>
      </div>
      <TransitionTabsSurface scope={DEV_RESOURCES_SCOPE} tabs={TABS} scrollablePanes contentClassName={PANE} />
    </div>
  )
}
